/**
 * The actions on one message, shown only when you reach for them (#1069
 * Story 2.5).
 *
 * Story 1.3 rendered "Edit" and "Delete" as permanent text actions under every
 * eligible bubble. In a conversation of any length that is a column of verbs
 * competing with the words. Every chat and mail client in the comparison set
 * hides the verbs until the reader reaches for a message, and this module is
 * that pattern in two halves:
 *
 *  - WEB: one "…" trigger per eligible message (`MessageActions`), hidden by
 *    OPACITY and never by absence — it stays in the tree, in the tab order and
 *    in the accessibility tree at all times (WCAG 2.1.1). It shows while the
 *    pointer is over the message block, while it has keyboard focus, and while
 *    its menu is open. On a coarse pointer (a tablet) there is no hover, so it
 *    sits at a reduced opacity instead. Click / Enter opens a small menu of the
 *    eligible verbs, trailing-aligned under the trigger; Escape closes it and
 *    returns focus to the trigger (WCAG 1.4.13), as does choosing an item; a
 *    mousedown outside or a scroll of the list closes it.
 *  - NATIVE: no "…" (it is the clutter in a smaller font). A long press on the
 *    bubble opens a small CHIP at the bubble's trailing corner
 *    (`NativeMessageActionsChip`): the same two items as the web menu, over a
 *    transparent backdrop so a touch anywhere else dismisses it — no dim, no
 *    Cancel, never a timeout (owner decisions D-g/D-h, 2026-09-10; the first
 *    cut was the `ActionSheetIOS` / `Alert.alert` bottom sheet, which wrapped
 *    two verbs in a modal frame far from the message). Screen-reader users
 *    get the same verbs WITHOUT the gesture as accessibility actions
 *    (`nativeAccessibilityActions`), which is what makes a long press an
 *    acceptable primary path at all. Because the gesture is hidden, the FIRST
 *    eligible message on a device carries a one-time hint (D-i; copy below,
 *    ownership in `MessageList`).
 *
 * THE PATTERN IS `ScopeStepOverflowMenu.tsx`, the house overflow menu: the
 * 32pt trigger with `MoreHorizontal`, `measureInWindow` + a clamp to the
 * window, a `createPortal(document.body)` menu at `position: fixed` so the
 * dock's overflow cannot clip it, `Pressable` items with a hovered fill. NOT
 * copied from it: its open/close coordination (`ScopeMenuCoordinationContext`,
 * typed to scope step ids), its confirmation dialog (the panel already owns
 * the delete confirmation) and its native `Modal` branch (the chip below is
 * the native answer). ADDED, because the scope menu lacks them: Escape, focus
 * restore, `menu` / `menuitem` roles, and — after code review, 2026-09-10 —
 * focus INTO the menu on open with arrow-key navigation. Without that last
 * part the roles were a lie: a portal appends the menu to the end of
 * `document.body`, so a keyboard user who opened it had to Tab through the
 * rest of the page to reach Edit.
 *
 * The chip is the house overflow menu's NATIVE branch (a transparent `Modal`
 * backdrop, the menu at the anchor's measured position, tap-backdrop-to-close)
 * with the web menu's items inside it, so the two platforms rhyme and a copy
 * change lands once. There is no bottom sheet here any more: the
 * `ActionSheetIOS` / `Alert.alert` first cut was replaced by the chip on
 * 2026-09-10 (D-g), and nothing in this module imports either.
 *
 * ELIGIBILITY IS NOT DECIDED HERE. `MessageRow` computes `offerEdit` and
 * `offerDelete` exactly as it did before this story and passes the verdict in;
 * this module only presents it. A message that offers nothing renders no
 * trigger, gets no long press and declares no accessibility actions — the row
 * does not mount any of this for it. The server remains the control (NFR8).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontWeight } from '../../theme/typography';
import { Download, Edit, MoreHorizontal, Trash2 } from '../icons';

import type {
  AccessibilityActionEvent,
  AccessibilityActionInfo,
  PressableStateCallbackType,
  ViewStyle,
} from 'react-native';

/** The trigger is a 32pt square, the scope menu's size. */
const TRIGGER_SIZE = 32;
/**
 * The menu's geometry, exported so the position tests name the numbers they
 * assert instead of re-typing them.
 */
export const MESSAGE_ACTIONS_MENU_GEOMETRY = {
  itemHeight: 44,
  minWidth: 168,
  /**
   * ⚠️ A MAXIMUM, ADDED BECAUSE THE MENU OUTGREW ITS OWN CLAMP (#1069 Story
   * 4.2, found in the app 2026-09-14). `resolveMenuPosition` kept the menu on
   * screen by measuring `minWidth` — fine while every item read "Edit" or
   * "Delete". Story 4.2 added "Download 1000005760.jpg", the menu grew to fit
   * it, and the clamp went on protecting a width the menu no longer had: it
   * ran off the right edge of the window with the file names unreadable.
   *
   * So the menu is now BOUNDED as well as clamped, and long names ellipsize in
   * the MIDDLE — a file is identified by its extension as much as its start,
   * and "Download SiteScope-Ed…" hides the one thing that says what it is.
   */
  maxWidth: 280,
  /** `paddingVertical` × 2 on the menu. */
  padding: 8,
  /** Air between the anchor and the menu. */
  gap: 4,
  /** Clamp inset from the window edge. */
  edge: 8,
} as const;
/**
 * The tallest the menu may draw: the window less both clamp insets.
 *
 * Paired with `maxHeight` on `styles.menu` and a `ScrollView` around the items,
 * so a menu that does not fit scrolls instead of spilling off screen. The two
 * must agree, which is why the positioner calls this rather than repeating the
 * arithmetic.
 */
export const menuMaxHeight = (windowHeight: number): number =>
  Math.max(MENU_ITEM_HEIGHT * 2, windowHeight - WINDOW_EDGE * 2);

/**
 * The bubble's inner padding and its body line height (`MessageRow.tsx`,
 * `styles.bubble.padding` = `spacing.md`, `styles.msgBody.lineHeight`). The
 * anchor uses them to sit on the body's first line; change them together.
 */
const BUBBLE_PADDING = spacing.md;
const BODY_LINE_HEIGHT = 20;
const MENU_MIN_WIDTH = MESSAGE_ACTIONS_MENU_GEOMETRY.minWidth;
const MENU_MAX_WIDTH = MESSAGE_ACTIONS_MENU_GEOMETRY.maxWidth;
const MENU_ITEM_HEIGHT = MESSAGE_ACTIONS_MENU_GEOMETRY.itemHeight;
const MENU_GAP = MESSAGE_ACTIONS_MENU_GEOMETRY.gap;
const WINDOW_EDGE = MESSAGE_ACTIONS_MENU_GEOMETRY.edge;

/**
 * What the trigger sits at on a coarse pointer, where there is no hover to
 * reveal it. Reduced emphasis, never invisible: one small muted glyph per
 * eligible message is what Gmail does on a tablet, and it is far less than two
 * verbs. There is no opacity token in the theme; this is the one place the
 * value is used, so it lives here and is exported for the test that pins it.
 *
 * ⚠️ THIS VALUE IS A CONTRAST FLOOR, NOT A TASTE SETTING (code review,
 * 2026-09-10). The glyph fades toward the bubble as the opacity drops, and the
 * governing rule for it is WCAG SC 1.4.11 (non-text contrast, 3:1) because it
 * is a UI component, NOT SC 1.4.3's 4.5:1, which AC 14 pinned for the
 * full-opacity case by choice. Measured on `textSecondary` over
 * `surfaceMessage`: 0.55 gave **2.13:1** and failed; 0.80 gives **3.25:1** and
 * passes with margin. 4.5:1 is unreachable at ANY reduction, because the glyph
 * only reaches 4.70:1 at full opacity. Do not lower this without re-measuring.
 */
export const MESSAGE_ACTIONS_COARSE_OPACITY = 0.8;

/** The two labels a listener hears, distinguishing own from another's. */
export const messageActionsLabel = (
  isOwn: boolean,
  authorName: string,
): string =>
  isOwn
    ? 'More actions for your message'
    : `More actions for the message from ${authorName}`;

/**
 * `(hover: none)` is the media query for "this device cannot hover". Guarded
 * so that a missing `matchMedia` (SSR, the test harness, an old browser) means
 * "fine pointer" — the default, hidden-until-hover behaviour.
 */
export const isCoarsePointer = (): boolean => {
  if (typeof window === 'undefined') return false;
  const mm = (window as { matchMedia?: unknown }).matchMedia;
  if (typeof mm !== 'function') return false;
  try {
    return (
      (mm as (q: string) => { matches?: boolean })('(hover: none)')?.matches ===
      true
    );
  } catch {
    return false;
  }
};

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') return null;
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

/**
 * Where the menu goes: trailing-aligned under the trigger, flipped above it
 * when the bottom of the window is too close (the last message in a docked
 * list), and clamped to the window on every edge so it stays inside the
 * viewport at the 360pt dock minimum. The scope menu's arithmetic, with the
 * height taken from how many items this menu actually has.
 */
export function resolveMenuPosition(
  anchorRect: { top: number; left: number; width: number; height: number },
  itemCount: number,
  /**
   * How wide this menu can actually get.
   *
   * ⚠️ NOT ALWAYS `MENU_MIN_WIDTH`, AND ASSUMING SO IS THE BUG (found in the
   * app, 2026-09-14). A menu of "Edit" and "Delete" is exactly the minimum; a
   * menu carrying "Download 1000005760.jpg" grows to the maximum, and clamping
   * it by the minimum let it hang off the right edge of the window. Clamping
   * everything by the MAXIMUM instead would drag the common two-item menu 112px
   * away from the corner it is anchored to — so the caller says which it is.
   */
  menuWidth: number = MENU_MIN_WIDTH,
): { top: number; left: number } {
  const screen = Dimensions.get('window');
  // ⚠️ CAPPED, BECAUSE THE MENU CAN NOW BE TALLER THAN THE WINDOW (code
  // review, 2026-09-14). `maxWidth` was added on 2026-09-14 when file names
  // grew the menu past its clamp; the same change grew it in the OTHER axis
  // and that half was not revisited. `itemCount` includes one Download per
  // file, so an own message with the maximum six files is 8 items — 360pt,
  // taller than a phone in landscape. Positioning from the uncapped height
  // made the flip-above branch overflow too, and `Math.max(WINDOW_EDGE, top)`
  // then pinned it to the top with the last Downloads simply off screen and no
  // way to scroll to them. Position from what will actually be drawn.
  const menuHeight = Math.min(
    itemCount * MENU_ITEM_HEIGHT + MESSAGE_ACTIONS_MENU_GEOMETRY.padding,
    menuMaxHeight(screen.height),
  );
  let top = anchorRect.top + anchorRect.height + MENU_GAP;
  let left = anchorRect.left + anchorRect.width - MENU_MIN_WIDTH;
  if (top + menuHeight > screen.height - WINDOW_EDGE) {
    top = anchorRect.top - menuHeight - MENU_GAP;
  }
  if (left + menuWidth > screen.width - WINDOW_EDGE) {
    left = screen.width - menuWidth - WINDOW_EDGE;
  }
  return {
    top: Math.max(WINDOW_EDGE, top),
    left: Math.max(WINDOW_EDGE, left),
  };
}

/**
 * Where a long press should hang the chip (code review, 2026-09-10).
 *
 * Normally that is the bubble's rect, and the chip lands at its trailing
 * corner. But a bubble can be TALLER THAN THE WINDOW — a long message with
 * `Show more` expanded is the easy case — and then the arithmetic in
 * `resolveMenuPosition` degenerates: "below the bottom" is off screen, so it
 * flips to "above the top", which is negative, so it clamps to the window
 * edge, and the chip appears at the top of the screen with no visible
 * relationship to the finger that summoned it.
 *
 * ⚠️ Trimming the rect to the visible slice does NOT fix this, and the first
 * attempt at this fix did exactly that before a mutation test showed it
 * changed no output at all: `top + height` is the same number either way, and
 * the flip branch collapses to `WINDOW_EDGE` from both sides. Measured across
 * four representative rects, raw and trimmed agreed on every one.
 *
 * So when the bubble does not fit, the anchor becomes the TOUCH POINT: a
 * zero-height rect at the finger, keeping the bubble's horizontal extent so
 * the chip stays trailing-aligned. That is where iMessage puts it, and it is
 * the only point on a screen-filling bubble that means anything.
 */
export function anchorForChip(rect: AnchorRect, touchY?: number): AnchorRect {
  const screen = Dimensions.get('window');
  const fits = rect.top >= 0 && rect.top + rect.height <= screen.height;
  if (fits || typeof touchY !== 'number' || !Number.isFinite(touchY)) {
    return rect;
  }
  return { top: touchY, left: rect.left, width: rect.width, height: 0 };
}

/** A DOM node, if the ref resolved to one. Guarded: the test harness's refs are not. */
const containsTarget = (node: unknown, target: unknown): boolean => {
  const n = node as { contains?: (t: unknown) => boolean } | null;
  return typeof n?.contains === 'function' && n.contains(target) === true;
};

const keyOf = (event: unknown): string | undefined => {
  const e = event as { key?: string; nativeEvent?: { key?: string } } | null;
  return e?.key ?? e?.nativeEvent?.key;
};

const itemStyle = (state: PressableStateCallbackType) => [
  styles.menuItem,
  (state as { hovered?: boolean }).hovered ? styles.menuItemHovered : null,
];

interface MessageActionItemsProps {
  messageId: string;
  offerEdit: boolean;
  offerDelete: boolean;
  /**
   * One entry per attached file (#1069 Story 4.2, owner decision 2026-09-14).
   *
   * ⚠️ ONE PER FILE, NOT ONE "DOWNLOAD ALL". A message carries up to six, and
   * the person almost always wants a particular one — the deck, not the six
   * photos beside it. A single entry would fire six browser downloads at once
   * on web and offer no way to take just one. It reads correctly to a screen
   * reader too: "Download SiteScope-Edge.pptx" says which.
   */
  downloads?: readonly { id: string; name: string }[];
  onDownload?: (fileId: string) => void;
  /** Edit DISABLED, not hidden, on the message in the composer (AC 10). */
  editing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** Closes whatever holds the items, then runs the action. */
  onChoose: (action: () => void) => void;
  /**
   * Web only: hands each rendered item back by position so the menu can move
   * focus into it and walk it with the arrow keys. Positions are dense (Edit
   * is 0 only when Edit is offered), which is what the menu's index
   * arithmetic assumes.
   */
  registerItem?: (index: number, node: unknown) => void;
}

/**
 * The two verbs, shared by the web menu and the native chip: `Edit` then
 * `Delete`, a `menuitem` each, a hover fill on web, Delete in the error
 * colour. A NO-OP `onPress` while Edit is disabled, not an absent one. On a
 * device it makes no difference: a disabled `Pressable` declines the touch and
 * the chip's own `onStartShouldSetResponder` claims it, so the backdrop never
 * sees it. It exists for the TEST HARNESS, whose `Pressable` is a string mock
 * that honours neither `disabled` nor responder semantics: there, a press with
 * no handler of its own climbs to the nearest ancestor that has one — the
 * backdrop — and a test would see the chip close. Do not remove it citing
 * "no onPress"; the harness case is the one it serves.
 */
const noop = () => {};

const MessageActionItems: React.FC<MessageActionItemsProps> = ({
  messageId,
  offerEdit,
  offerDelete,
  downloads = [],
  onDownload,
  editing,
  onEdit,
  onDelete,
  onChoose,
  registerItem,
}) => (
  <>
    {offerEdit ? (
      <Pressable
        ref={node => registerItem?.(0, node)}
        style={itemStyle}
        onPress={editing ? noop : () => onChoose(onEdit)}
        disabled={editing}
        testID={`correspondence-message-edit-${messageId}`}
        accessibilityRole="menuitem"
        accessibilityLabel="Edit"
        accessibilityState={{ disabled: editing }}
      >
        <View style={styles.menuItemIcon} accessibilityElementsHidden>
          <Edit
            size={16}
            color={editing ? colors.textMuted : colors.textPrimary}
          />
        </View>
        <Text
          style={[styles.menuItemText, editing && styles.menuItemTextMuted]}
        >
          Edit
        </Text>
      </Pressable>
    ) : null}
    {offerDelete ? (
      <Pressable
        ref={node => registerItem?.(offerEdit ? 1 : 0, node)}
        style={itemStyle}
        onPress={() => onChoose(onDelete)}
        testID={`correspondence-message-delete-${messageId}`}
        accessibilityRole="menuitem"
        accessibilityLabel="Delete"
      >
        <View style={styles.menuItemIcon} accessibilityElementsHidden>
          <Trash2 size={16} color={colors.error} />
        </View>
        <Text style={[styles.menuItemText, styles.menuItemDeleteText]}>
          Delete
        </Text>
      </Pressable>
    ) : null}
    {downloads.map((file, i) => (
      <Pressable
        key={file.id}
        // ⚠️ AFTER Edit and Delete, so their indices never move: the keyboard
        // walk and the two hard-coded `registerItem` calls above both depend on
        // that, and a download appearing first would silently renumber them.
        ref={node =>
          registerItem?.((offerEdit ? 1 : 0) + (offerDelete ? 1 : 0) + i, node)
        }
        style={itemStyle}
        onPress={() => onChoose(() => onDownload?.(file.id))}
        testID={`correspondence-message-download-${file.id}`}
        accessibilityRole="menuitem"
        accessibilityLabel={`Download ${file.name}`}
      >
        <View style={styles.menuItemIcon} accessibilityElementsHidden>
          <Download size={16} color={colors.textPrimary} />
        </View>
        <Text
          style={styles.menuItemText}
          numberOfLines={1}
          // The extension is half of what identifies a file, so the middle
          // goes rather than the end.
          ellipsizeMode="middle"
        >
          {`Download ${file.name}`}
        </Text>
      </Pressable>
    ))}
  </>
);

interface MessageActionsProps {
  messageId: string;
  /** The label case: own vs an administrator on another's. */
  isOwn: boolean;
  authorName: string;
  /** The row's verdicts, unchanged since Story 1.3. */
  offerEdit: boolean;
  offerDelete: boolean;
  /**
   * The pointer is over the MESSAGE BLOCK. Owned by the row, because the
   * block is the row's element; the trigger's own focus and open state are
   * owned here.
   */
  hovered: boolean;
  /** This message is the one in the composer: Edit disabled, not hidden. */
  editing?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /**
   * One menu entry per attached file (#1069 Story 4.2, owner 2026-09-14).
   * A message someone ELSE sent offers neither Edit nor Delete, and its files
   * still have to be reachable — so this is the one action the menu can carry
   * on its own.
   */
  downloads?: readonly { id: string; name: string }[];
  onDownload?: (fileId: string) => void;
}

/**
 * WEB ONLY. The row renders this as the last child of the bubble on web and
 * nothing at all on native (see the module comment for why).
 */
export const MessageActions: React.FC<MessageActionsProps> = ({
  messageId,
  isOwn,
  authorName,
  offerEdit,
  offerDelete,
  hovered,
  editing = false,
  onEdit,
  onDelete,
  downloads = [],
  onDownload,
}) => {
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  /**
   * `null` until the anchor has been measured. The menu is not rendered at a
   * fixed position before then (code review, 2026-09-10): this used to start
   * at `{top: 0, left: 0}` and open BEFORE measuring, and because
   * react-native-web's `measureInWindow` is a `setTimeout(…, 0)` around
   * `getBoundingClientRect`, every first open painted the menu at the
   * top-left corner of the window and then jumped. `MessageRow`'s `openChip`
   * already had this right for native; this is the same order.
   */
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Once per mount: a device does not change pointer class mid-thread.
  const [coarse] = useState(isCoarsePointer);
  const anchorRef = useRef<View>(null);
  const triggerRef = useRef<View>(null);
  const menuRef = useRef<View>(null);
  /**
   * The rendered items by position, for focus-into and the arrow keys. A
   * `Map` rather than an array because a numeric index into an array is a
   * `security/detect-object-injection` sink, and this one is keyed by a value
   * the arrow keys compute.
   */
  const itemRefs = useRef(new Map<number, unknown>());
  /** Which item the arrow keys are on. Only `focusItem` moves it. */
  const activeIndex = useRef(0);
  /** Where the anchor was when the menu opened, to notice if it moves. */
  const anchorAt = useRef<{ top: number; left: number } | null>(null);

  const itemCount =
    (offerEdit ? 1 : 0) + (offerDelete ? 1 : 0) + downloads.length;
  const triggerId = `correspondence-message-actions-${messageId}`;
  const menuId = `correspondence-message-actions-menu-${messageId}`;

  /**
   * Focus goes BACK to the trigger when the menu closes because the user
   * finished with it (Escape, or a choice). Not on an outside click or a
   * scroll: there the user has already moved on, and yanking focus back to
   * a "…" they did not touch is the surprise WCAG 1.4.13 exists to prevent.
   */
  const restoreFocus = useCallback(() => {
    const t = triggerRef.current as unknown as { focus?: () => void } | null;
    if (typeof t?.focus === 'function') t.focus();
  }, []);

  const close = useCallback(
    (restore: boolean) => {
      setOpen(false);
      anchorAt.current = null;
      if (restore) restoreFocus();
    },
    [restoreFocus],
  );

  /** Move focus onto a menu item, wrapping at both ends. */
  const focusItem = useCallback(
    (index: number) => {
      if (itemCount === 0) return;
      const wrapped = (index + itemCount) % itemCount;
      activeIndex.current = wrapped;
      const node = itemRefs.current.get(wrapped) as {
        focus?: () => void;
      } | null;
      if (typeof node?.focus === 'function') node.focus();
    },
    [itemCount],
  );

  /**
   * Measure FIRST, then open at the resolved position. A platform that cannot
   * measure (no DOM: the test harness) opens with `menuPosition` null, which
   * renders the menu inline under the trigger instead of portaled — the same
   * tree a reader gets, minus the portal.
   */
  const openMenu = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor?.measureInWindow) {
      anchorAt.current = null;
      setMenuPosition(null);
      setOpen(true);
      return;
    }
    anchor.measureInWindow((x, y, width, height) => {
      anchorAt.current = { top: y, left: x };
      setMenuPosition(
        resolveMenuPosition(
          { top: y, left: x, width, height },
          itemCount,
          // A menu carrying file names is the wide one; see the parameter.
          downloads.length > 0 ? MENU_MAX_WIDTH : MENU_MIN_WIDTH,
        ),
      );
      setOpen(true);
    });
  }, [itemCount, downloads.length]);

  const toggle = useCallback(() => {
    if (open) {
      close(true);
      return;
    }
    openMenu();
  }, [close, open, openMenu]);

  /**
   * Focus goes INTO the menu when it opens (code review, 2026-09-10). The
   * menu is portaled to the end of `document.body`, so without this a
   * keyboard user who pressed Enter on the trigger had to Tab through every
   * remaining focusable element on the page to reach Edit — the roles said
   * `menu` and the behaviour was nothing of the kind.
   */
  useEffect(() => {
    if (!open) return;
    // Directly, not deferred: the items' ref callbacks have already run by
    // the time an effect fires, so there is nothing to wait for, and a
    // deferred focus would be a frame the user could type into.
    focusItem(0);
  }, [focusItem, open]);

  const choose = useCallback(
    (action: () => void) => {
      close(true);
      action();
    },
    [close],
  );

  /**
   * Escape, as a PROP on the menu and on the trigger rather than a document
   * listener (the dock's rule), so the component test can reach it.
   */
  const onKeyDown = useCallback(
    (event: unknown) => {
      if (!open) return;
      const key = keyOf(event);
      const e = event as {
        preventDefault?: () => void;
        stopPropagation?: () => void;
        shiftKey?: boolean;
      };
      const claim = () => {
        e.preventDefault?.();
        e.stopPropagation?.();
      };
      if (key === 'Escape') {
        claim();
        close(true);
        return;
      }
      /**
       * Tab leaves the menu the way the ARIA menu pattern says: close it and
       * put focus back on the trigger, WITHOUT claiming the event, so the
       * browser's own Tab then carries on from the trigger to whatever
       * follows it in the document. Claiming it would strand the user, and
       * letting it through without closing would drop them at the end of
       * `document.body`, where the portal lives.
       */
      if (key === 'Tab') {
        close(true);
        return;
      }
      if (key === 'ArrowDown') {
        claim();
        focusItem(activeIndex.current + 1);
        return;
      }
      if (key === 'ArrowUp') {
        claim();
        focusItem(activeIndex.current - 1);
        return;
      }
      if (key === 'Home') {
        claim();
        focusItem(0);
        return;
      }
      if (key === 'End') {
        claim();
        focusItem(itemCount - 1);
      }
    },
    [activeIndex, close, focusItem, itemCount, open],
  );

  // A mousedown anywhere but the trigger or the menu closes it. One menu open
  // at a time follows: opening another message's "…" is a mousedown outside.
  useEffect(() => {
    if (!open) return undefined;
    if (
      typeof document === 'undefined' ||
      typeof document.addEventListener !== 'function'
    ) {
      return undefined;
    }
    const onMouseDown = (event: { target?: unknown }) => {
      if (
        containsTarget(triggerRef.current, event.target) ||
        containsTarget(anchorRef.current, event.target) ||
        containsTarget(menuRef.current, event.target)
      ) {
        return;
      }
      close(false);
    };
    document.addEventListener('mousedown', onMouseDown as EventListener);
    return () => {
      document.removeEventListener('mousedown', onMouseDown as EventListener);
    };
  }, [close, open]);

  // The menu is measured once and fixed to the window; if the list scrolls
  // under it, it would float over the wrong message. Close rather than chase.
  // Capture phase, because the list's scroller does not bubble its scroll.
  useEffect(() => {
    if (!open) return undefined;
    if (
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return undefined;
    }
    const onScrollOrResize = () => close(false);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [close, open]);

  /**
   * A scroll is not the only way the anchor moves under a `position: fixed`
   * menu (code review, 2026-09-10). Prepending a page of older messages
   * shifts every row and emits NO scroll event on web — `MessageList`'s own
   * header comment records that react-native-web 0.21.2 has no
   * `maintainVisibleContentPosition` and that a prepend at offset 0 leaves
   * the offset at 0 — and a poll that withdraws a message above this one does
   * the same. The menu would then sit over a different message than the one
   * it acts on, which is a destructive-action hazard, not a cosmetic one. So
   * while it is open, watch the anchor and close if it has moved.
   */
  useEffect(() => {
    if (!open || anchorAt.current === null) return undefined;
    const anchor = anchorRef.current;
    if (
      !anchor?.measureInWindow ||
      typeof requestAnimationFrame !== 'function' ||
      typeof cancelAnimationFrame !== 'function'
    ) {
      return undefined;
    }
    let frame = 0;
    let stopped = false;
    const check = () => {
      if (stopped) return;
      anchor.measureInWindow((x, y) => {
        if (stopped) return;
        const at = anchorAt.current;
        if (at && (Math.abs(x - at.left) > 1 || Math.abs(y - at.top) > 1)) {
          close(false);
          return;
        }
        frame = requestAnimationFrame(check);
      });
    };
    frame = requestAnimationFrame(check);
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
    };
  }, [close, open]);

  const revealed = hovered || focused || open;
  const opacity = revealed ? 1 : coarse ? MESSAGE_ACTIONS_COARSE_OPACITY : 0;

  const menuContent = (
    <MessageActionItems
      messageId={messageId}
      offerEdit={offerEdit}
      offerDelete={offerDelete}
      downloads={downloads}
      onDownload={onDownload}
      editing={editing}
      onEdit={onEdit}
      onDelete={onDelete}
      onChoose={choose}
      registerItem={(index, node) => {
        itemRefs.current.set(index, node);
      }}
    />
  );

  const createPortal = getCreatePortal();
  const portalTarget =
    typeof document !== 'undefined' ? document.body ?? null : null;
  // Only portal to a MEASURED position: a fixed menu at an unmeasured one is
  // the top-left-corner flash this component used to have.
  const portal =
    open && menuPosition !== null && createPortal && portalTarget
      ? { render: createPortal, target: portalTarget }
      : null;
  const portaled = portal !== null;

  const menu = open ? (
    <View
      ref={menuRef}
      testID={menuId}
      nativeID={menuId}
      accessibilityRole="menu"
      onStartShouldSetResponder={() => true}
      style={[
        styles.menu,
        portaled && menuPosition
          ? [
              styles.menuFixed,
              { top: menuPosition.top, left: menuPosition.left },
            ]
          : styles.menuInline,
      ]}
      {...({ onKeyDown } as Record<string, unknown>)}
    >
      <ScrollView
        style={{ maxHeight: menuMaxHeight(Dimensions.get('window').height) }}
        // The menu is short in the common case; a scroller that only engages
        // when it has to must not steal the bounce or the wheel until then.
        bounces={false}
        showsVerticalScrollIndicator
      >
        {menuContent}
      </ScrollView>
    </View>
  ) : null;

  return (
    <>
      <View ref={anchorRef} style={styles.anchor}>
        <Pressable
          ref={triggerRef}
          style={[
            styles.trigger,
            focused && styles.triggerFocusVisible,
            { opacity },
          ]}
          /**
           * Invisible must also mean intangible (code review, 2026-09-10). An
           * `opacity: 0` element is still hit-tested, so at rest this 32pt
           * disc sat over the trailing end of the body's first line and ate
           * clicks and text selection there. Reveal is driven by hover on the
           * message BLOCK, not on the trigger, so nothing is lost by taking
           * it out of hit-testing until it is actually showing.
           */
          pointerEvents={revealed || coarse ? 'auto' : 'none'}
          onPress={toggle}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          testID={triggerId}
          accessibilityRole="button"
          accessibilityLabel={messageActionsLabel(isOwn, authorName)}
          accessibilityState={{ expanded: open }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
          {...({
            onKeyDown,
            'aria-haspopup': 'menu',
            ...(open ? { 'aria-controls': menuId } : {}),
          } as Record<string, unknown>)}
        >
          <MoreHorizontal size={18} color={colors.textSecondary} />
        </Pressable>
        {/* Without a portal (no DOM: the test harness) the menu renders in
            place, so what a test reaches is the same tree the user gets. */}
        {menu && !portaled ? menu : null}
      </View>
      {menu && portal ? portal.render(menu, portal.target) : null}
    </>
  );
};

// ---------------------------------------------------------------------------
// NATIVE: the chip, the one-time hint copy, and the accessibility actions.
// ---------------------------------------------------------------------------

/** What VoiceOver / TalkBack hear when the chip opens (guarded: see below). */
export const NATIVE_MESSAGE_ACTIONS_ANNOUNCEMENT = 'Message actions';

/**
 * The one-time hint under the FIRST eligible message on a device (D-i). Two
 * copies: the viewer's own message, or — for an IT Administrator whose first
 * eligible message is somebody else's — a message they may only delete.
 */
export const LONG_PRESS_HINT_OWN =
  'Press and hold your message to edit or delete it.';
export const LONG_PRESS_HINT_ANY = 'Press and hold a message to delete it.';

/**
 * `AccessibilityInfo` is absent from the test harness's react-native mock and
 * `announceForAccessibility` is a no-op on web, so the call is guarded rather
 * than trusted.
 */
export const announceMessageActions = (): void => {
  const info = AccessibilityInfo as
    | { announceForAccessibility?: (text: string) => void }
    | undefined;
  if (typeof info?.announceForAccessibility === 'function') {
    info.announceForAccessibility(NATIVE_MESSAGE_ACTIONS_ANNOUNCEMENT);
  }
};

interface NativeMessageActionsInput {
  offerEdit: boolean;
  offerDelete: boolean;
  /** Under edit: the chip shows Edit disabled; the actions list omits it. */
  editing?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

/** The verbs the accessibility actions offer, in order. */
const eligibleVerbs = ({
  offerEdit,
  offerDelete,
  editing = false,
  onEdit,
  onDelete,
}: NativeMessageActionsInput): {
  name: 'edit' | 'delete';
  label: string;
  run: () => void;
}[] => {
  const verbs: { name: 'edit' | 'delete'; label: string; run: () => void }[] =
    [];
  if (offerEdit && !editing) {
    verbs.push({ name: 'edit', label: 'Edit', run: onEdit });
  }
  if (offerDelete) {
    verbs.push({ name: 'delete', label: 'Delete', run: onDelete });
  }
  return verbs;
};

/**
 * The body wrapper's `accessibilityActions`: the eligible verbs, or `null`
 * when there are none so the row declares NOTHING (an empty list would still
 * be a declaration).
 */
export const nativeAccessibilityActions = (
  input: Omit<NativeMessageActionsInput, 'onEdit' | 'onDelete'>,
): AccessibilityActionInfo[] | null => {
  const verbs = eligibleVerbs({
    ...input,
    onEdit: () => {},
    onDelete: () => {},
  });
  return verbs.length > 0
    ? verbs.map(v => ({ name: v.name, label: v.label }))
    : null;
};

/** Dispatch a screen reader's chosen action to the same handlers as the chip. */
export const runNativeAccessibilityAction = (
  input: NativeMessageActionsInput,
  event: AccessibilityActionEvent,
): void => {
  const verb = eligibleVerbs(input).find(
    v => v.name === event.nativeEvent.actionName,
  );
  verb?.run();
};

export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface NativeMessageActionsChipProps extends NativeMessageActionsInput {
  messageId: string;
  /** The bubble's window rect, or `null` when it could not be measured. */
  anchorRect: AnchorRect | null;
  /** One entry per attached file (Story 4.2); see `MessageActionsProps`. */
  downloads?: readonly { id: string; name: string }[];
  onDownload?: (fileId: string) => void;
  onClose: () => void;
}

/**
 * NATIVE ONLY. Long press → this. A transparent full-screen `Modal` whose
 * backdrop closes on any touch (which is also what "scroll" and "long press
 * another message" become while it is open: one touch closes it, the user
 * repeats the gesture — iMessage's behaviour), `onRequestClose` for Android
 * Back, and the two items at the bubble's trailing corner, flipped above it
 * when the bottom of the window is too close. Nothing dims. Nothing times
 * out. The `Modal` is itself modal to VoiceOver and TalkBack, so the backdrop
 * carries a label to be the way out for a screen-reader user who opened it.
 */
export const NativeMessageActionsChip: React.FC<
  NativeMessageActionsChipProps
> = ({
  messageId,
  anchorRect,
  offerEdit,
  offerDelete,
  downloads = [],
  onDownload,
  editing = false,
  onEdit,
  onDelete,
  onClose,
}) => {
  const itemCount =
    (offerEdit ? 1 : 0) + (offerDelete ? 1 : 0) + downloads.length;
  const position = anchorRect
    ? resolveMenuPosition(
        anchorRect,
        itemCount,
        downloads.length > 0 ? MENU_MAX_WIDTH : MENU_MIN_WIDTH,
      )
    : { top: WINDOW_EDGE, left: WINDOW_EDGE };

  useEffect(() => {
    announceMessageActions();
  }, []);

  const choose = (action: () => void) => {
    onClose();
    action();
  };

  return (
    <Modal
      visible
      transparent
      /**
       * `none`, not `fade`: the row MOUNTS this on long press and UNMOUNTS it
       * on close, and React Native only animates the exit of a `Modal` whose
       * `visible` flips while mounted. A fade in with a hard cut out reads as
       * a glitch; an instant pop both ways is what "it pops in" asked for.
       */
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <Pressable
        style={styles.chipBackdrop}
        onPress={onClose}
        testID={`correspondence-message-actions-backdrop-${messageId}`}
        accessibilityRole="button"
        accessibilityLabel="Close message actions"
      >
        <View
          onStartShouldSetResponder={() => true}
          style={[styles.menu, styles.chip, position]}
          testID={`correspondence-message-actions-menu-${messageId}`}
          accessibilityRole="menu"
        >
          {/* Same cap as the web menu: six Downloads plus Edit and Delete is
              taller than a phone in landscape, and a menu whose last items are
              unreachable is worse than one that scrolls. */}
          <ScrollView
            style={{
              maxHeight: menuMaxHeight(Dimensions.get('window').height),
            }}
            bounces={false}
            showsVerticalScrollIndicator
          >
            <MessageActionItems
              messageId={messageId}
              offerEdit={offerEdit}
              offerDelete={offerDelete}
              downloads={downloads}
              onDownload={onDownload}
              editing={editing}
              onEdit={onEdit}
              onDelete={onDelete}
              onChoose={choose}
            />
          </ScrollView>
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  /**
   * The bubble's trailing corner, centred on the FIRST LINE of the body
   * (owner, 2026-09-10: "could these be centered vertically" — the first cut
   * floated it over the bubble's top edge the way Slack does, and on a
   * one-line bubble that read as misaligned). Centred on the first line is
   * vertically centred on a one-line bubble and stays at the top of a long
   * one instead of drifting into the middle of the text. Absolute, so nothing
   * else in the row moves (AC 12); trailing is the right edge in reading
   * order, on the viewer's own messages and on another's alike. Rendered as
   * the bubble's LAST child so it paints — and takes the pointer — above the
   * body text.
   */
  anchor: {
    position: 'absolute',
    top: BUBBLE_PADDING - (TRIGGER_SIZE - BODY_LINE_HEIGHT) / 2,
    right: 0,
    zIndex: 2,
  },
  /**
   * The disc FLOATS OVER the body text rather than sitting flush in it
   * (owner, 2026-09-10, at code review: "could we make it hover over the text
   * as opposed to sitting inline with it"). Same fill as the bubble, so it
   * reads as the same material, but lifted with a soft shadow and a hairline
   * edge: the last few characters of a full-width first line are still
   * covered while the pointer is there, and now they read as covered by
   * something on top rather than as a hole in the sentence.
   */
  trigger: {
    width: TRIGGER_SIZE,
    height: TRIGGER_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: TRIGGER_SIZE / 2,
    backgroundColor: colors.surfaceMessage,
    borderWidth: 1,
    borderColor: colors.borderLight,
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      } as unknown as ViewStyle,
      default: { elevation: 4 },
    }),
  },
  /**
   * The focus ring the research asked for and the first cut dropped (code
   * review, 2026-09-10). Opacity alone told a keyboard user the control
   * existed but not that it was theirs; this is the dock's `focusVisible`
   * discipline, in the one state where it matters.
   */
  triggerFocusVisible: {
    borderColor: colors.textSecondary,
  },
  menu: {
    minWidth: MENU_MIN_WIDTH,
    maxWidth: MENU_MAX_WIDTH,
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.cardBorderRadius,
    borderWidth: 1,
    borderColor: colors.borderLight,
    paddingVertical: spacing.xs,
    ...Platform.select({
      web: { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } as unknown as ViewStyle,
      default: { elevation: 8 },
    }),
  },
  /** Portaled to `document.body`: fixed to the measured window position. */
  menuFixed: {
    ...({ position: 'fixed' } as unknown as ViewStyle),
    zIndex: 999999,
  },
  /** The native chip's backdrop: the whole window, invisible, one touch closes. */
  chipBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  /** The native chip: `menu` placed at the measured window position. */
  chip: {
    position: 'absolute',
  },
  /** No portal: under the trigger, trailing-aligned, inside the anchor. */
  menuInline: {
    position: 'absolute',
    top: TRIGGER_SIZE + MENU_GAP,
    right: 0,
    zIndex: 999999,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    minHeight: MENU_ITEM_HEIGHT,
    ...Platform.select({ web: { cursor: 'pointer' as const }, default: {} }),
  },
  menuItemHovered: {
    backgroundColor: colors.backgroundSecondary,
  },
  menuItemIcon: {
    width: 16,
    height: 16,
    marginRight: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  menuItemText: {
    fontSize: 14,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  menuItemTextMuted: {
    color: colors.textMuted,
  },
  /**
   * `colors.error` on white is **3.55:1**, below SC 1.4.3's 4.5:1, and this
   * is a DELIBERATE, RECORDED exception, not an oversight, and not "fixed" to
   * grey (AC 14).
   *
   * ⚠️ The rationale AC 14 originally gave for it was wrong and was corrected
   * at code review (2026-09-10): it claimed a "14px+ semibold" label earning
   * WCAG's large-text exemption. The label is `fontWeight.medium` at 14px,
   * and neither medium nor semibold at 14px reaches the exemption's bar
   * (14pt bold, or 18pt). So the honest statement is simply this: red is the
   * destructive convention, the label is never the only signal (it sits
   * beside a `Trash2` icon inside a menu the reader opened deliberately, and
   * a confirmation dialog follows every press), and the value is pinned so a
   * token edit that drops it under SC 1.4.11's 3:1 floor for the icon is
   * caught. If that trade stops being acceptable, darken the token; do not
   * re-add a large-text claim the type does not support.
   */
  menuItemDeleteText: {
    color: colors.error,
  },
});
